<template>
  <v-navigation-drawer
    permanent
    :width="50"
    color="transparent"
    class="icon-strip-menu"
    id="icon-strip-menu"
    :style="{ zIndex: 8 }"
  >
    <!-- Logo -->
    <div class="logo-container">
      <MenuLogo @logoClick="handleLogoClick" :compact="true" />
    </div>

    <!-- Facility Name -->
    <v-tooltip v-if="facilityName" right :open-delay="300">
      <template v-slot:activator="{ on, attrs }">
        <div
          class="facility-name-container"
          v-bind="attrs"
          v-on="on"
        >
          <span class="facility-name">{{ facilityName }}</span>
        </div>
      </template>
      <span>{{ facilityName }}</span>
    </v-tooltip>

    <!-- Main Group Icons -->
    <div class="icon-list main-icons" role="menubar" aria-label="Main navigation">
      <v-tooltip
        v-for="group in mainGroups"
        :key="group.id"
        right
        :open-delay="300"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            class="icon-btn"
            :class="{
              'active': activeGroup === group.id,
              'has-open-palette': activeGroup === group.id && (isPrimaryPaletteOpen || isSecondaryPaletteOpen)
            }"
            :aria-expanded="activeGroup === group.id && isPrimaryPaletteOpen"
            :aria-controls="`palette-${group.id}`"
            :aria-label="`${group.title} menu`"
            :tabindex="focusedIndex === mainGroups.indexOf(group) ? 0 : -1"
            @click="handleGroupClick(group.id)"
            @keydown="handleKeyDown($event, mainGroups.indexOf(group))"
          >
            <v-icon :size="20">{{ group.icon }}</v-icon>
          </v-btn>
        </template>
        <span>{{ group.title }}</span>
      </v-tooltip>
    </div>

    <!-- Spacer pushes footer to bottom -->
    <v-spacer />

    <!-- Footer Group Icons (Settings) -->
    <div class="icon-list footer-icons" role="menubar" aria-label="Settings navigation">
      <v-tooltip
        v-for="group in footerGroups"
        :key="group.id"
        right
        :open-delay="300"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            class="icon-btn"
            :class="{
              'active': activeGroup === group.id,
              'has-open-palette': activeGroup === group.id && (isPrimaryPaletteOpen || isSecondaryPaletteOpen)
            }"
            :aria-expanded="activeGroup === group.id && isPrimaryPaletteOpen"
            :aria-controls="`palette-${group.id}`"
            :aria-label="`${group.title} menu`"
            @click="handleGroupClick(group.id)"
          >
            <v-icon :size="20">{{ group.icon }}</v-icon>
            <span
              v-if="isPinned && activeGroup === group.id"
              class="pin-indicator"
            />
          </v-btn>
        </template>
        <span>{{ group.title }}</span>
      </v-tooltip>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { mapGetters } from 'vuex';
import { getGroupsSorted } from '@Share/config/menuConfig';
import MenuLogo from '@Share/components/MenuLogo.vue';

export default {
  name: 'IconStripMenu',

  components: {
    MenuLogo,
  },

  data() {
    return {
      focusedIndex: 0,
    };
  },

  computed: {
    ...mapGetters('GENERAL', {
      activeGroup: 'GET_ACTIVE_GROUP',
      isPrimaryPaletteOpen: 'GET_IS_PRIMARY_PALETTE_OPEN',
      isSecondaryPaletteOpen: 'GET_IS_SECONDARY_PALETTE_OPEN',
      isPinned: 'GET_IS_PINNED',
    }),

    /**
     * Facility name from store (set when facility is selected)
     */
    facilityName() {
      return this.$store.getters.getAssetName || '';
    },

    /**
     * Main groups (position: 'main'), sorted by order
     * Filtered by user permissions via v-has directive usage
     */
    mainGroups() {
      return getGroupsSorted('main');
    },

    /**
     * Footer groups (position: 'footer'), typically just Settings
     */
    footerGroups() {
      return getGroupsSorted('footer');
    },

    /**
     * All groups combined for keyboard navigation
     */
    allGroups() {
      return [...this.mainGroups, ...this.footerGroups];
    },
  },

  methods: {
    /**
     * Handle group icon click
     * Dispatches OPEN_GROUP action to Vuex
     */
    handleGroupClick(groupId) {
      this.$store.dispatch('GENERAL/OPEN_GROUP', groupId);
      this.$emit('group-click', { groupId });
    },

    /**
     * Handle logo click - navigate home or show confirmation
     */
    handleLogoClick() {
      // Close any open palettes first
      this.$store.dispatch('GENERAL/CLOSE_ALL_PALETTES');
      // Emit for parent to handle navigation
      this.$emit('logo-click');
    },

    /**
     * Keyboard navigation handler
     * Implements roving tabindex pattern per WCAG 2.1
     */
    handleKeyDown(event, currentIndex) {
      const groups = this.allGroups;
      let newIndex = currentIndex;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          newIndex = Math.min(currentIndex + 1, groups.length - 1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          newIndex = Math.max(currentIndex - 1, 0);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          this.handleGroupClick(groups[currentIndex].id);
          return;
        case 'Escape':
          event.preventDefault();
          this.$store.dispatch('GENERAL/CLOSE_ALL_PALETTES');
          return;
        case 'Home':
          event.preventDefault();
          newIndex = 0;
          break;
        case 'End':
          event.preventDefault();
          newIndex = groups.length - 1;
          break;
        default:
          return;
      }

      if (newIndex !== currentIndex) {
        this.focusedIndex = newIndex;
        // Focus the new button
        this.$nextTick(() => {
          const buttons = this.$el.querySelectorAll('.icon-btn');
          if (buttons[newIndex]) {
            buttons[newIndex].focus();
          }
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.icon-strip-menu {
  // Override Vuetify defaults
  &.v-navigation-drawer {
    border-radius: 0;
    overflow: visible !important;

    // Remove border
    .v-navigation-drawer__border {
      display: none !important;
    }
  }

  // Dark gradient background (from wizard-style-guide)
  background: linear-gradient(180deg, #0a2626 0%, #061a1a 50%, #021a1a 100%) !important;

  // Layout
  display: flex;
  flex-direction: column;
  height: 100%;

  .logo-container {
    padding: var(--ds-space-sm) var(--ds-space-sm);
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 60px;
  }

  .facility-name-container {
    padding: 0 var(--ds-space-xs) var(--ds-space-sm);
    display: flex;
    justify-content: center;
    align-items: center;
    border-bottom: 1px solid var(--ds-border-subtle);
    margin-bottom: var(--ds-space-xs);
    cursor: default;

    .facility-name {
      font-size: 9px;
      font-weight: 500;
      color: rgba(255, 255, 255, 0.6);
      text-transform: uppercase;
      letter-spacing: 0.5px;
      text-align: center;
      line-height: 1.2;
      max-width: 42px;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      word-break: break-word;
    }
  }

  .icon-list {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: var(--ds-space-sm) 0;
    gap: var(--ds-space-xs);

    &.footer-icons {
      padding-bottom: var(--ds-space-md);
    }
  }

  .icon-btn {
    width: var(--ds-nav-icon-btn-size);
    height: var(--ds-nav-icon-btn-size);
    border-radius: var(--ds-radius-md);
    position: relative;
    transition: all var(--ds-transition-default);

    // Default icon color
    .v-icon {
      color: rgba(255, 255, 255, 0.6);
      transition: all var(--ds-transition-default);
    }

    // Hover state
    &:hover {
      background-color: var(--ds-surface-hover);

      .v-icon {
        color: rgba(255, 255, 255, 0.87);
      }
    }

    // Focus state for keyboard navigation
    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px var(--v-primary-base);
    }

    // Active state (group is selected)
    &.active {
      background-color: var(--ds-surface-active);

      .v-icon {
        color: var(--v-secondary-base);
      }

      // Left border indicator
      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 3px;
        height: 20px;
        background-color: var(--v-secondary-base);
        border-radius: 0 var(--ds-radius-sm) var(--ds-radius-sm) 0;
      }
    }

    // Has open palette state
    &.has-open-palette {
      background-color: rgba(3, 98, 76, 0.3);
    }

    // Pin indicator dot
    .pin-indicator {
      position: absolute;
      top: 6px;
      right: 6px;
      width: 6px;
      height: 6px;
      border-radius: var(--ds-radius-full);
      background-color: var(--v-primary-base);
    }
  }
}

// Mobile responsive
@media (max-width: 768px) {
  .icon-strip-menu {
    .icon-btn {
      width: 48px;
      height: 48px;
    }
  }
}
</style>
